import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api/requests';
import log from '../../logger';

// Fetch categories
export const fetchCategories = createAsyncThunk(
  'category/fetchCategories',
  async ({ page = 1, limit = 10, filters = {} } = {}, { rejectWithValue }) => {
    try {
      log.info('Fetching categories with page:', page, 'and limit:', limit);
      const response = await api.categories.getAll({ page, limit, filters });
      if (response.data.status) {
        log.info('Categories fetched successfully:', response.data.data);
        return response.data.data;
      } else {
        log.warn('Failed to fetch categories:', response.data.message);
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error fetching categories:', error);
      return rejectWithValue(error.response?.data?.message || 'Error fetching categories');
    }
  }
);

// Search categories with keyword
export const searchCategories = createAsyncThunk(
  'category/searchCategories',
  async ({ keyword, page = 1, limit = 10, filters = {} } = {}, { rejectWithValue }) => {
    try {
      log.info('Searching categories with keyword:', keyword);
      const response = await api.categories.search({ keyword, page, limit, ...filters });
      if (response.data.status) {
        log.info('Categories search completed successfully:', response.data.data);
        return response.data.data;
      } else {
        log.warn('Failed to search categories:', response.data.message);
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error searching categories:', error);
      return rejectWithValue(error.response?.data?.message || 'Error searching categories');
    }
  }
);

// Fetch a single category by ID
export const fetchCategoryById = createAsyncThunk(
  'category/fetchCategoryById',
  async (id, { rejectWithValue }) => {
    try {
      log.info('Fetching category with ID:', id);
      const response = await api.categories.getById(id);
      if (response.data.status) {
        log.info('Category fetched successfully:', response.data.data);
        return response.data.data;
      } else {
        log.warn('Failed to fetch category:', response.data.message);
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error fetching category:', error);
      return rejectWithValue(error.response?.data?.message || 'Error fetching category');
    }
  }
);

// Create category
export const createCategory = createAsyncThunk(
  'category/createCategory',
  async (formData, { rejectWithValue }) => {
    try {
      log.info('Creating category with data:', formData);
      const response = await api.categories.create(formData);
      if (response.data.status) {
        log.info('Category created successfully:', response.data.data);
        return response.data.data;
      } else {
        log.warn('Failed to create category:', response.data.message);
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error creating category:', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to create category');
    }
  }
);

// Update category
export const updateCategory = createAsyncThunk(
  'category/updateCategory',
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      log.info('Updating category with ID:', id, 'and data:', formData);
      const response = await api.categories.update(id, formData);
      if (response.data.status) {
        log.info('Category updated successfully:', response.data.data);
        return response.data.data;
      } else {
        log.warn('Failed to update category:', response.data.message);
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error updating category:', error);
      return rejectWithValue(error.response?.data?.message || 'Error updating category');
    }
  }
);

// Delete category
export const deleteCategory = createAsyncThunk(
  'category/deleteCategory',
  async (id, { rejectWithValue }) => {
    try {
      log.info('Deleting category with ID:', id);
      const response = await api.categories.delete(id);
      if (response.data.status) {
        log.info('Category deleted successfully:', id);
        return id;
      } else {
        log.warn('Failed to delete category:', response.data.message);
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error deleting category:', error);
      return rejectWithValue(error.response?.data?.message || 'Error deleting category');
    }
  }
);

// Bulk delete categories
export const bulkDeleteCategories = createAsyncThunk(
  'category/bulkDeleteCategories',
  async (ids, { rejectWithValue }) => {
    try {
      await Promise.all(ids.map(id => api.categories.delete(id)));
      return ids;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Error deleting categories');
    }
  }
);

// Bulk activate/deactivate categories
export const bulkUpdateCategoryStatus = createAsyncThunk(
  'category/bulkUpdateStatus',
  async ({ ids, status }, { rejectWithValue }) => {
    try {
      await api.categories.bulkUpdateStatus(ids, status);
      return { ids, status };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Error updating category status');
    }
  }
);

const initialState = {
  items: [],
  total: 0,
  page: 1,
  pages: 0,
  limit: 10,
  loading: false,
  error: null
};

const categorySlice = createSlice({
  name: 'category',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch categories
      .addCase(fetchCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.items = action.payload.categories || [];
        state.total = parseInt(action.payload.total || 0);
        state.page = parseInt(action.payload.page || 1);
        state.pages = parseInt(action.payload.pages || 0);
        state.limit = parseInt(action.payload.limit || 10);
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Search categories
      .addCase(searchCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(searchCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.items = action.payload.categories || [];
        state.total = parseInt(action.payload.total || 0);
        state.page = parseInt(action.payload.page || 1);
        state.pages = parseInt(action.payload.pages || 0);
        state.limit = parseInt(action.payload.limit || 10);
      })
      .addCase(searchCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create category
      .addCase(createCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.items.push(action.payload);
      })
      .addCase(createCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update category
      .addCase(updateCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        const index = state.items.findIndex(item => item._id === action.payload._id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
      })
      .addCase(updateCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete category
      .addCase(deleteCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.items = state.items.filter(item => item._id !== action.payload);
      })
      .addCase(deleteCategory.rejected, (state) => {
        state.loading = false;
      })
      // Bulk delete categories
      .addCase(bulkDeleteCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(bulkDeleteCategories.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(bulkDeleteCategories.rejected, (state) => {
        state.loading = false;
      })
      .addCase(bulkUpdateCategoryStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(bulkUpdateCategoryStatus.fulfilled, (state, action) => {
        state.loading = false;
        const { ids, status } = action.payload;
        state.items = state.items.map(item => ids.includes(item._id) ? { ...item, status } : item);
      })
      .addCase(bulkUpdateCategoryStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export default categorySlice.reducer; 